import {
  IChartProps,
  IFeedbackDetailsResponseModel,
  IStatisticsResponseModel,
} from "../../system-performance/models/chart";
import {
  ModelType,
  StatisticsDataInput,
  TimePeriod,
  eventType,
} from "../../../../../API";
import { useEffect, useMemo, useState } from "react";

import ApexOptions from "../../../../../common/components/apexchart/ApexChartModels";
import { MITIGATED_RISKS_ENUM } from "../../../../data-hub/components/models/common/events.constants";
import uniq from "lodash/uniq";
import { useGetStatisticsData } from "../../system-health/hooks/useGetStatisticsData";
import { useTheme } from "@mui/material";
import { modelToServiceMap } from "../../../../../common/variables/common";

export const useFeedbackDetailsChart = (
  timePeriod: TimePeriod,
  locations: string[]
): IChartProps => {
  const theme = useTheme();

  const options: ApexOptions = {
    chart: {
      type: "bar",
      stacked: true,
      height: 300,
      toolbar: {
        show: false,
      },
      fontFamily: "Inter",
    },
    plotOptions: {
      bar: {
        horizontal: true,
        dataLabels: {
          total: {
            enabled: true,
            offsetX: 0,
            style: {
              fontSize: "13px",
              fontWeight: 900,
            },
          },
        },
      },
    },
    stroke: {
      width: 1,
      colors: ["#fff"],
    },
    theme: {
      mode: theme.palette.mode,
    },
    tooltip: {
      enabled: true,
      custom: function ({ series, dataPointIndex, w }) {
        const total = series.reduce((sum: number, s: number[]) => {
          const value = s[dataPointIndex];

          return sum + (value || 0);
        }, 0);

        const modelName = w.globals.categoryLabels[dataPointIndex];

        return `
          <div class="apexcharts-tooltip-box">
            ${
              modelName
                ? `<div style="margin-bottom: 8px;">
              <strong>${modelName}</strong>
            </div>`
                : ""
            }
            <div style="margin-bottom: 8px;">
              <strong>Total: ${total}</strong>
            </div>
            <div style="border-top: 1px solid #ccc; padding-top: 8px;">
              ${series
                .map((s: number[], i: number) => {
                  const value = s[dataPointIndex];

                  if (value && value > 0) {
                    const seriesName =
                      w.globals.seriesNames[i] || `Series ${i + 1}`;

                    return `<div>${seriesName}: <strong>${value}</strong></div>`;
                  }

                  return "";
                })
                .join("")}
            </div>
          </div>
        `;
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      showForNullSeries: false,
      showForZeroSeries: false,
      showForSingleSeries: true,
      fontFamily: "Inter",
    },
    colors: [
      "#FFA500",
      "#3E8DF3",
      "#8B4513",
      "#8A2BE2",
      "#F150FF",
      "#FF4500",
      "#808080",
      "#A0522D",
      "#32CD32",
    ],
  };

  const filterParams = useMemo(
    (): Pick<
      StatisticsDataInput,
      "eventType" | "models" | "timePeriod" | "locations"
    > => ({
      eventType: eventType.feedbackDetails,
      models: [],
      timePeriod,
      locations,
    }),
    [timePeriod, locations]
  );

  const { data, loading, refetching, refetchChartData } =
    useGetStatisticsData<IStatisticsResponseModel[]>(filterParams);

  const [chartSeries, setChartProps] = useState<
    ApexAxisChartSeries | ApexNonAxisChartSeries | undefined
  >([]);
  const [chartOptions, setChartOptions] = useState<ApexOptions>(options);

  useEffect(() => {
    const parsed = data;

    const series = generateChartSeries(parsed);
    const extendedOptions = generateChartCategories(parsed);

    setChartProps(series);

    setChartOptions(extendedOptions);
  }, [data, theme.palette.mode]);

  const generateChartSeries = (
    response: IFeedbackDetailsResponseModel[]
  ): ApexOptions["series"] => {
    const filteredResponse = response.filter(item => item.mitigatedRisk);

    const models = uniq(filteredResponse.map(s => s.model)) || [];

    const modelOrder = ["fire", "smoke", "gasleak", "liquidleak", "hardhat"];

    const sortedModels = models.sort((a, b) => {
      return (
        modelOrder.indexOf(a.toLowerCase()) -
        modelOrder.indexOf(b.toLowerCase())
      );
    });

    const series = Object.values(MITIGATED_RISKS_ENUM).map(
      (mitigatedRisk: string) => ({
        name: mitigatedRisk,
        data: sortedModels.map(model => {
          return filteredResponse
            .filter(
              entry =>
                entry.model === model &&
                entry.mitigatedRisk
                  ?.split(";")
                  .map(r => r.trim())
                  .includes(mitigatedRisk)
            )
            .reduce((sum, entry) => sum + Number(entry.count), 0);
        }),
      })
    );

    return series;
  };

  const generateChartCategories = (
    response: IFeedbackDetailsResponseModel[]
  ): ApexOptions => {
    if (!Array.isArray(response) || response.length === 0) {
      return options;
    }

    const filteredResponse = response.filter(item => item.mitigatedRisk);

    const models = uniq(filteredResponse.map(s => s.model)) || [];

    const modelOrder = ["fire", "smoke", "gasleak", "liquidleak", "hardhat"];

    const sortedModels = models.sort((a, b) => {
      return (
        modelOrder.indexOf(a.toLowerCase()) -
        modelOrder.indexOf(b.toLowerCase())
      );
    });

    const sortedCategories = sortedModels
      .map((model, index) => {
        const modelName = modelToServiceMap[model as ModelType];

        return `${index + 1}. ${modelName}`;
      })
      .filter(Boolean);

    const extendedOptions: ApexOptions = {
      ...options,
      xaxis: {
        ...options.xaxis,
        categories: sortedCategories,
        min: 0,
        labels: {
          formatter: val => (Number.isInteger(val) ? String(val) : ""),
        },
      },
      yaxis: {
        labels: {
          offsetX: -22,
          align: "left",
        },
      },
    };

    return extendedOptions;
  };

  return {
    series: chartSeries,
    options: chartOptions,
    loading,
    refetching,
    refetchChartData,
    showNoData:
      !loading && !refetching && data?.filter(d => d.count).length === 0,
  };
};
